import type { ComponentPropsWithRef } from 'react';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useTranslation } from 'react-i18next';

import { clsx } from '~/styles/utils';

import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Button/Button';
import { Spinner } from '../Spinner/Spinner';
import { usePreviewUrl } from './FileUpload.hooks';
import { avatarOverlayStyle, avatarOverlayVisibleStyle, avatarSizeVar, avatarWrapperStyle } from './UploadedAvatar.css';

type Props = ComponentPropsWithRef<'div'> & {
  disabled?: boolean;
  error?: boolean;
  onRemove: () => void;
  onRetry: () => void;
  preview?: Blob | string;
  /** Number is a fraction between 0 and 1, `null` is uploading by an unknown amount, `undefined` is idle. */
  progress?: number | null;
  size?: number;
};

export const UploadedAvatar = ({ disabled, error, onRemove, onRetry, preview, progress, size = 96, ...delegated }: Props) => {
  const { t } = useTranslation(['common']);

  const previewUrl = usePreviewUrl(preview);

  const isUploading = progress !== undefined;
  const isBusy = error || isUploading;

  return (
    <div
      {...delegated}
      className={clsx(avatarWrapperStyle, delegated.className)}
      style={{ ...assignInlineVars({ [avatarSizeVar]: `${size}px` }), ...delegated.style }}
    >
      <Avatar size={size} src={previewUrl} />

      <div className={clsx(avatarOverlayStyle, isBusy && avatarOverlayVisibleStyle)}>
        {isUploading && <Spinner size={22} />}

        {error && (
          <Button aria-label={t('common:fileUpload.retry')} disabled={disabled} emphasis="tertiary" icon="Refresh" onClick={onRetry} />
        )}

        {!isUploading && (
          <Button aria-label={t('common:fileUpload.remove')} disabled={disabled} emphasis="tertiary" icon="Trash" onClick={onRemove} />
        )}
      </div>
    </div>
  );
};
